import { useState, useRef, useEffect } from "react";
import { MessageSquare, X, Send, Headphones, Loader2 } from "lucide-react";
import { API } from "@/lib/api";
import { COMPANY } from "@/data/company";

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [sid, setSid] = useState(null);
  const [status, setStatus] = useState("bot");
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const endRef = useRef(null);
  const pollRef = useRef(null);

  const scroll = () => setTimeout(() => endRef.current?.scrollIntoView({ behavior: "smooth" }), 60);

  const start = async () => {
    if (sid) return sid;
    const r = await fetch(`${API}/chat/start`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ visitor_name: "Visiteur" }),
    });
    const d = await r.json();
    setSid(d.session_id);
    setMessages([{ role: "assistant", content: `Bonjour 👋 Je suis l'assistant de ${COMPANY.name}. Comment puis-je vous aider avec votre projet de maçonnerie ou d'aménagement extérieur ?` }]);
    return d.session_id;
  };

  useEffect(() => {
    if (open && !sid) start();
  }, [open]); // eslint-disable-line

  // Poll for agent/system messages when a human is involved
  useEffect(() => {
    if (!sid || !open) return;
    if (status === "bot") { clearInterval(pollRef.current); return; }
    pollRef.current = setInterval(async () => {
      const r = await fetch(`${API}/chat/${sid}/messages`);
      const d = await r.json();
      setStatus(d.status);
      setMessages(d.messages.map((m) => ({ role: m.role, content: m.content })));
    }, 3500);
    return () => clearInterval(pollRef.current);
  }, [sid, status, open]);

  const requestAgent = async () => {
    const s = await start();
    const r = await fetch(`${API}/chat/${s}/request-agent`, { method: "POST" });
    const d = await r.json();
    setStatus(d.status);
    setMessages((m) => [...m, { role: "system", content: d.message }]);
    scroll();
  };

  const send = async () => {
    const text = input.trim();
    if (!text || streaming) return;
    const s = await start();
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }]);
    scroll();

    if (status !== "bot") {
      // human handling — just persist the user message
      await fetch(`${API}/chat/message`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: s, content: text }),
      });
      return;
    }

    setStreaming(true);
    setMessages((m) => [...m, { role: "assistant", content: "" }]);
    try {
      const resp = await fetch(`${API}/chat/message`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: s, content: text }),
      });
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop();
        for (const p of parts) {
          const line = p.replace(/^data: /, "").trim();
          if (!line) continue;
          try {
            const j = JSON.parse(line);
            if (j.delta) {
              setMessages((m) => {
                const copy = [...m];
                copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + j.delta };
                return copy;
              });
              scroll();
            }
          } catch { /* ignore */ }
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "Souci technique. Appelez-nous au " + COMPANY.phone };
        return copy;
      });
    } finally {
      setStreaming(false);
      scroll();
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen(!open)}
        data-testid="chat-toggle"
        className="fixed bottom-5 right-5 z-[60] h-14 w-14 rounded-full bg-primary text-white shadow-xl flex items-center justify-center hover:scale-105 transition-transform"
        aria-label="Ouvrir le chat"
      >
        {open ? <X size={24} /> : <MessageSquare size={24} />}
      </button>

      {open && (
        <div data-testid="chat-panel" className="fixed bottom-24 right-5 z-[60] w-[calc(100vw-2.5rem)] sm:w-[380px] h-[540px] max-h-[75vh] bg-white rounded-2xl shadow-2xl border border-border flex flex-col overflow-hidden">
          <div className="bg-ink text-white px-4 py-3 flex items-center gap-3">
            <div className="h-9 w-9 rounded-full bg-primary flex items-center justify-center font-heading font-black">S</div>
            <div className="flex-1">
              <p className="font-heading font-bold leading-tight">Assistant SMT Provence</p>
              <p className="text-[11px] text-white/60">
                {status === "with_agent" ? "Conseiller en ligne" : status === "waiting_agent" ? "En attente d'un conseiller" : "Réponse instantanée"}
              </p>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-muted/40">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[80%] px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
                  m.role === "user" ? "bg-primary text-white rounded-br-sm"
                  : m.role === "system" ? "bg-accent text-ink border border-border w-full text-center text-[13px]"
                  : m.role === "agent" ? "bg-ink text-white rounded-bl-sm"
                  : "bg-white text-ink border border-border rounded-bl-sm"
                }`}>
                  {m.content || (streaming && <Loader2 className="animate-spin" size={14} />)}
                </div>
              </div>
            ))}
            <div ref={endRef} />
          </div>

          {status === "bot" && (
            <button onClick={requestAgent} data-testid="chat-request-agent" className="mx-4 mb-2 mt-1 inline-flex items-center justify-center gap-2 text-xs font-semibold text-primary border border-primary/40 rounded-full py-2 hover:bg-accent transition">
              <Headphones size={14} /> Parler à un conseiller
            </button>
          )}

          <div className="p-3 border-t border-border flex items-center gap-2">
            <input
              data-testid="chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Écrivez votre message…"
              className="flex-1 rounded-full border border-border px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-primary"
            />
            <button onClick={send} disabled={streaming} data-testid="chat-send" className="h-10 w-10 rounded-full bg-primary text-white flex items-center justify-center disabled:opacity-50">
              <Send size={17} />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
